"use client";

import { Box, BoxProps } from "@chakra-ui/react";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import { useMemo, useState } from "react";
import "keen-slider/keen-slider.min.css";

import { Image as ImageType } from "@/types/image";
import { getImagesByType, getPlaceholderImageSrc } from "@/utils/file";
import { Skeleton } from "@/components/ui/skeleton";
import ImageSliderDots from "./ImageSliderDots";
import ImageSliderNavigation from "./ImageSliderNavigation";

interface Props extends BoxProps {
  images: ImageType[];
  isNavigationHidden?: boolean;
  isDotsHidden?: boolean;
}

const UniversalImageSlider = ({
  images,
  isDotsHidden = false,
  isNavigationHidden = false,
  aspectRatio = "16/9",
  ...props
}: Props) => {
  const imagesUrls = useMemo(
    () => (getImagesByType({ images }, "gallery") || []) as string[],
    [images],
  );

  const isSingle = imagesUrls.length <= 1;

  const [keenLoaded, setKeenLoaded] = useState(false);
  const [firstImageLoaded, setFirstImageLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const ready = isSingle
    ? firstImageLoaded
    : keenLoaded && firstImageLoaded;

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    renderMode: "performance",
    slides: {
      perView: 1,
      spacing: 0,
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setKeenLoaded(true);
    },
  });

  return (
    <Box position="relative" w="100%" {...props}>
      <Box
        position="relative"
        w="100%"
        aspectRatio={aspectRatio}
        overflow="hidden"
      >
        {!ready && (
          <Box position="absolute" inset={0} zIndex={1}>
            <Skeleton w="100%" h="100%" />
          </Box>
        )}

        {isSingle && (
          <Image
            src={imagesUrls[0] || getPlaceholderImageSrc()}
            alt="Image"
            fill
            sizes="100vw"
            priority
            onLoad={() => {
              setFirstImageLoaded(true)
            }}
            style={{
              objectFit: "cover",
              opacity: ready ? 1 : 0,
            }}
          />
        )}

        {!isSingle && (
          <div
            ref={sliderRef}
            className="keen-slider"
            style={{
              height: "100%",
              opacity: ready ? 1 : 0,
            }}
          >
            {imagesUrls.map((image, index) => {
              const isFirst = index === 0;

              return (
                <Box
                  key={index}
                  className="keen-slider__slide"
                  position="relative"
                  w="100%"
                  h="100%"
                >
                  <Image
                    src={image || getPlaceholderImageSrc()}
                    alt={`Image ${index + 1}`}
                    fill
                    sizes="100vw"
                    priority={isFirst}
                    onLoad={() => isFirst && setFirstImageLoaded(true)}
                    style={{ objectFit: "cover" }}
                  />
                </Box>
              );
            })}
          </div>
        )}
      </Box>

      {!isSingle && ready && instanceRef.current && (
        <>
          {!isNavigationHidden && (
            <ImageSliderNavigation
              slider={instanceRef.current}
              currentSlide={currentSlide}
            />
          )}

          {!isDotsHidden && (
            <ImageSliderDots
              slider={instanceRef.current}
              currentSlide={currentSlide}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default UniversalImageSlider;
